import { computed, inject, Service, signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import MiniSearch from 'minisearch';
import { CookieService } from 'ngx-cookie-service';
import { combineLatest, filter, of, pipe, switchMap, tap } from 'rxjs';

import { GeoService } from '@ideals/services/geo';
import { APP_CONFIG, AppPage, Cultures, IStringMap, ITimeZone } from '@ideals/types';
import { rxMethod, tapResponse } from '@ideals/utils';

import { COUNTRY_CODE_COOKIE, DEFAULT_COUNTRY_CODE, SPACE_OR_PUNCTUATION, SPLIT_HIEROGLYPHS_ONLY } from '../models/constants';
import { IHelpCountry } from '../models/interfaces';
import { Articles, HelpCountries, Videos } from '../models/types';
import { HelpService } from '../services/help.service';

interface LoadHelpDataParams {
  readonly language: string;
}

type Terms = string[];

function tokenizePunctuation(term: string): Terms {
  return term.split(SPACE_OR_PUNCTUATION);
}

function tokenizeBySymbol(term: string): Terms {
  return tokenizePunctuation(term).reduce<string[]>((acc, str) => {
    acc.push(...str.match(SPLIT_HIEROGLYPHS_ONLY));

    return acc;
  }, []);
}

@Service()
export class HelpStore {
  readonly #appConfig = inject(APP_CONFIG);
  readonly #cookieService = inject(CookieService);
  readonly #geoService = inject(GeoService);
  readonly #helpService = inject(HelpService);
  readonly #translateService = inject(TranslateService);

  readonly #articles = signal<Articles>([]);
  readonly #videos = signal<Videos>([]);

  readonly #loadArticles = rxMethod<void>(
    pipe(
      tap(() => this.articlesLoading.set(true)),
      switchMap(() => this.#helpService.getArticles()
        .pipe(
          tapResponse({
            next: (articles) => {
              this.#articles.set(articles);
              this.articlesLoading.set(false);
            },
            error: () => {
              this.articlesLoading.set(false);
            },
          }),
        )),
    ),
  );

  readonly #loadCountries = rxMethod<void>(
    pipe(
      tap(() => this.countriesLoading.set(true)),
      switchMap(() => combineLatest([
        this.#userLocation
          ? of(this.#userLocation)
          : this.#geoService.getTimeZone().pipe(
            tap((userLocation) => {
              this.#userLocation = userLocation;
            }),
          ),
        this.#countryPhonesMapping
          ? of(this.#countryPhonesMapping)
          : this.#helpService.getCountryPhonesMapping().pipe(
            tap((countryPhonesMapping) => {
              this.#countryPhonesMapping = countryPhonesMapping;
            }),
          ),
        this.#helpService.getHelpCountries().pipe(
          tap((countries) => this.countries.set(countries)),
        ),
      ])
        .pipe(
          tapResponse({
            next: ([userLocation, countryPhonesMapping, countries]) => {
              const countryCode = this.#cookieService.get(COUNTRY_CODE_COOKIE)
                || countryPhonesMapping[userLocation.countryCode]
                || DEFAULT_COUNTRY_CODE;

              this.selectedCountry.set(countries.find((country) => country.countryCode === countryCode) || countries[0]);
              this.countriesLoading.set(false);
            },
            error: () => {
              this.countriesLoading.set(false);
            },
          }),
        )),
    ),
  );

  readonly #loadSearchIndex = rxMethod<void>(
    pipe(
      switchMap(() => this.#helpService.getSearchIndex()
        .pipe(
          tap((data) => {
            const searchIndex = new MiniSearch({
              fields: ['title', 'description', 'body'],
              storeFields: ['title', 'url'],
              // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison
              tokenize: this.#translateService.currentLang() === Cultures.CN
                ? tokenizeBySymbol
                : tokenizePunctuation,
              searchOptions: {
                boost: { title: 10 },
                prefix: true,
              },
            });

            searchIndex.addAll(data);
            this.searchIndex.set(searchIndex);
          }),
        )),
    ),
  );

  readonly #loadVideos = rxMethod<void>(
    pipe(
      tap(() => this.videosLoading.set(true)),
      switchMap(() => this.#helpService.getVideos()
        .pipe(
          tapResponse({
            next: (videos) => {
              this.#videos.set(videos);
              this.videosLoading.set(false);
            },
            error: () => {
              this.videosLoading.set(false);
            },
          }),
        )),
    ),
  );

  #countryPhonesMapping: IStringMap;
  #language: string;
  #userLocation: ITimeZone;

  readonly appPage = signal<AppPage>(undefined);
  readonly articlesLoading = signal(false);
  readonly countries = signal<HelpCountries>([]);
  readonly countriesLoading = signal(false);
  readonly searchIndex = signal<MiniSearch>(undefined);
  readonly selectedCountry = signal<IHelpCountry>(undefined);
  readonly videosLoading = signal(false);

  readonly filteredArticles = computed(() => {
    const page = this.appPage();

    return this.#articles().filter((item) => item.pages.split(/\s*[\s,]\s*/).includes(page));
  });
  readonly filteredVideos = computed(() => this.appPage() === 'login' ? [] : this.#videos());

  readonly loadHelpData = rxMethod<LoadHelpDataParams>(
    pipe(
      filter(({ language }: LoadHelpDataParams) => language !== this.#language),
      tap(({ language }: LoadHelpDataParams) => {
        this.#language = language;

        if (!this.appPage()) {
          this.setAppPage(this.#appConfig.appPage);
        }

        this.#loadCountries();
        this.#loadArticles();
        this.#loadVideos();

        if (!this.searchIndex()) {
          this.#loadSearchIndex();
        }
      }),
    ),
  );

  selectCountry(country: IHelpCountry): void {
    this.selectedCountry.set(country);
  }

  setAppPage(appPage: AppPage): void {
    this.appPage.set(appPage);
  }
}
